import {
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Space } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useParams } from "react-router-dom";
import { appStore } from "src/appStore";
import { CatalogsTree } from "src/business/catalog/CatalogsTree";
import {
  loadCatalogFilterMetadata,
  QueryBuilderFiltersManager,
  QueryBuilderManagerFacade,
} from "src/business/queryBuilder";
import {
  catalogQueryBuilderTemplatesLoader,
  createCatalogQueryBuilderTemplates,
  deleteCatalogQueryBuilderTemplate,
} from "src/business/queryBuilder/api/filterTemplatesApi";
import { onError } from "src/common/onError";
import { BlankLink } from "src/components/BlankLink";
import { ClosableTagsList } from "src/components/ClosableTag";
import { LeftCollapsedPanels } from "src/components/LeftCollapsedPanels";
import { LoaderBox } from "src/components/LoaderBox";
import { MdmTable } from "src/components/MdmTable";
import { PageLayout } from "src/components/PageLayout";
import { PageSubheader } from "src/components/PageSubheader";
import { QuerySearch } from "src/components/QuerySearch/QuerySearch";
import { RtColumnsSettingsButton } from "src/components/ReactiveTable/components/RtColumnsSettings";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import * as styles from "./RecordsPage.module.less";
import { RecordsPageActions } from "./RecordsPageActions";
import { RecordsData, recordsPageStore as pageStore } from "./recordsPageStore";

export const RecordsPage: React.FC = observer(() => {
  const { id = "" } = useParams();
  React.useEffect(() => {
    pageStore.init(id).catch(onError);
    return () => pageStore.dispose();
  }, [id]);
  return (
    <PageLayout title={pageStore.title}>
      <LoaderBox
        remoteData={pageStore.data}
        drawReady={(data) => <Inner data={data} />}
      />
    </PageLayout>
  );
});

const Inner: React.FC<{ data: RecordsData }> = observer(({ data }) => {
  const { table, catalog, tree } = data;
  const queryBuilderManager = React.useMemo(
    () =>
      new QueryBuilderFiltersManager({
        metadataLoader: () => loadCatalogFilterMetadata(catalog.id),
        setFilter: (filter) =>
          table.changeSingleFilter("queryBuilderFilter", filter),
        templatesLoader: catalogQueryBuilderTemplatesLoader(catalog.id),
        deleteTemplate: deleteCatalogQueryBuilderTemplate(catalog.id),
        createTemplate: createCatalogQueryBuilderTemplates(catalog.id),
      }),
    [catalog.id, table],
  );

  React.useEffect(() => {
    queryBuilderManager.init().catch(onError);
    return () => {
      queryBuilderManager.dispose();
    };
  }, [queryBuilderManager]);

  const { filters } = table;

  const handleReloadTable = () => {
    table.reload().catch(onError);
  };

  const handleOpenNewDraft = () => {
    queryBuilderManager.openNewDraft();
  };

  const handleOpenTemplatesModal = () => {
    queryBuilderManager.setTemplatesModalOpen(true);
  };

  const onClearTreeFilter = () => {
    tree.setSelectedKey(null);
    table.changeFilters({
      ...table.filters,
      classifierId: undefined,
      classId: undefined,
    });
  };
  const onNode = (classifierId?: string, classId?: string) => {
    table.changeFilters({
      ...table.filters,
      classifierId,
      classId,
    });
  };
  // нужно реагировать на очистку фильтров извне
  React.useEffect(() => {
    if (!filters?.classifierId && tree.activeNode) {
      tree.setSelectedKey(null);
    }
  }, [filters?.classifierId]);

  const filterTags = [];

  if (tree.activeNode) {
    filterTags.push({
      key: "tree",
      label: tree.activeNode.name,
      onClose: onClearTreeFilter,
    });
  }

  if (queryBuilderManager.hasAppliedFilters) {
    filterTags.push(...queryBuilderManager.filterTags);
  }

  return (
    <LeftCollapsedPanels
      leftTitle={
        <Space>
          <FilterOutlined />
          <span>Фильтр</span>
        </Space>
      }
      leftPart={<CatalogsTree store={tree} onChangeNode={onNode} />}
      leftClosed
    >
      <div className={styles.box}>
        <PageSubheader title={catalog.name} />
        <div className={styles.toolsTop}>
          <QuerySearch
            initialValue={filters?.query ?? ""}
            onChange={(value) =>
              table.changeFilters({ ...table.filters, query: value })
            }
          />
          <RtColumnsSettingsButton store={table} />
          <Button icon={<ReloadOutlined />} onClick={handleReloadTable} />
          <RecordsPageActions
            actions={catalog.actions}
            catalogId={pageStore.catalogId}
          />
        </div>
        <div className={styles.tools}>
          <ClosableTagsList
            tags={filterTags}
            action={
              <Button
                size="small"
                type="text"
                icon={<PlusOutlined />}
                onClick={handleOpenNewDraft}
              />
            }
          />
          {appStore.isMetadataEditor && (
            <BlankLink
              className={styles.settingsLink}
              href={makeUrl(PageUrl.managementCatalogEdit, {
                id: catalog.id,
              })}
            >
              <Space>
                <SettingOutlined />
                Перейти в настройки
              </Space>
            </BlankLink>
          )}
          <Button
            type="link"
            icon={<FilterOutlined />}
            onClick={handleOpenTemplatesModal}
          >
            Шаблоны
          </Button>
        </div>
        <div className={styles.tableBox}>
          <MdmTable
            store={table}
            columns={pageStore.columns}
            rowClick={{
              type: "link",
              builder: (row) => makeUrl(PageUrl.editRecord, { id: row.id }),
            }}
            resizableColumns
          />
        </div>
        <QueryBuilderManagerFacade manager={queryBuilderManager} />
      </div>
    </LeftCollapsedPanels>
  );
});
